import React, {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
} from "react";
import { Modal } from "react-responsive-modal";
import "react-responsive-modal/styles.css";
import ActionButtons from "../Components/ActionButtons";

type DownloadAppModalContextValue = {
  openDownloadModal: () => void;
  closeDownloadModal: () => void;
};

const DownloadAppModalContext =
  createContext<DownloadAppModalContextValue | null>(null);

export function useDownloadAppModal() {
  const ctx = useContext(DownloadAppModalContext);
  if (!ctx) {
    throw new Error(
      "useDownloadAppModal must be used within DownloadAppModalProvider"
    );
  }
  return ctx;
}

export function DownloadAppModalProvider({
  children,
}: {
  children: React.ReactNode;
}) {
  const [open, setOpen] = useState(false);
  const openDownloadModal = useCallback(() => setOpen(true), []);
  const closeDownloadModal = useCallback(() => setOpen(false), []);

  const value = useMemo(
    () => ({ openDownloadModal, closeDownloadModal }),
    [openDownloadModal, closeDownloadModal]
  );

  return (
    <DownloadAppModalContext.Provider value={value}>
      {children}
      <Modal
        open={open}
        onClose={closeDownloadModal}
        center
        styles={{
          modal: {
            borderRadius: "18px",
            boxShadow: "0 8px 30px rgba(0, 0, 0, 0.12)",
            padding: "36px 32px",
            maxWidth: "460px",
          },
        }}
      >
        <div className="text-center font-poppins">
          <div className="flex justify-center mb-4">
            <div className="w-14 h-14 rounded-2xl bg-[#0B7F47]/10 flex items-center justify-center">
              <svg
                width="28"
                height="28"
                viewBox="0 0 24 24"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M12 3v12m0 0l-4-4m4 4l4-4M5 21h14"
                  stroke="#0B7F47"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </div>
          </div>
          <h3 className="text-xl md:text-2xl font-semibold text-[#0A0A0A] mb-2">
            Download the AfriPay app
          </h3>
          <p className="text-sm text-[#637381] mb-6">
            Borderless payments in your pocket — available on iOS and Android.
          </p>
          <div className="flex justify-center" onClick={closeDownloadModal}>
            <ActionButtons />
          </div>
        </div>
      </Modal>
    </DownloadAppModalContext.Provider>
  );
}
